import { model, graphics } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import { Style, ShapeStyle, TextStyle } from "../style";
import type { OverlayKind, RenderStats } from "../stats";

//
// # `DrawTo`
//
// The "backend" or "target" interface for drawing.
//
// The `DrawTo` interface has two "parts":
// - The core primitive drawing API, which adds a single primitive to the target.
// - The groups and layering API, which allows for nested drawing and grouping.
//
// The core drawing API largely maps onto the `GenAlphaModel` `graphics` types,
// and is responsible for applying the `Style` types defined in this package.
//
// The layering API has two methods: `group` and `layer`.
// Both of which take a function/ closure to run.
// They both are expected to generate a "child context" (eg a sub-group),
// run the closure against that child, and then clean up the child context.
//
// The difference between `group` and `layer` is that `group` creates
// the new sub-group with a provided transform.
// Layer creates a group without transforming. It is effectively a
// shorthand for `group(IDENTITY, fn)`.
// This is often helpful for z-axis ordering in drawing "layers".
//
export interface DrawTo {
  //
  // World transform information, available to all redraw callees.
  // `scale` is the schematic-to-page scale factor (world units -> screen pixels).
  // `viewport` is the visible region in schematic coordinates, or null if unknown
  // (e.g. inside sprites where the viewport depends on instantiation position).
  readonly scale: number;
  readonly viewport: model.Bbox | null;

  // Rendering context, available to all redraw callees.
  // These are set per-frame from LayoutContent before rendering, so that
  // entity redraw methods can access them without walking the entity parent
  // chain back to the root content. This decouples rendering from the entity
  // tree structure, enabling shared entity trees across instances.
  readonly pdk: schema.pdk.PDK | null;
  readonly hiddenLayers: ReadonlySet<number>;

  // Layout display options — shared reference set at content-load time,
  // mutated in-place by toggle methods. Null when no layout is active.
  readonly layoutDisplay: LayoutDisplayOptions | null;

  // Schematic label visibility — shared reference set at content-load time,
  // mutated in-place by toggle methods. Null when no schematic is active.
  readonly schematicDisplay: SchematicDisplayOptions | null;

  // Draw within a child group.
  // Cleans up and exits the child after `fn` completes.
  group(xform: model.Xform, fn: RunFn): unknown;
  layer(fn: RunFn): unknown;

  // Tag the primitive draws inside `fn` as belonging to the given overlay kind.
  // Primitive calls (bbox, path, text, etc.) made during `fn` will be counted
  // against `stats.overlays[kind].drawn`. Overlays are drawings outside the
  // sprite cache — routes, labels, highlights, outlines.
  overlay(kind: OverlayKind, fn: RunFn): unknown;

  // Called by overlay drawers that do their own viewport/size culling and
  // skip a draw. Bumps the current overlay kind's `culled` / `sizeCulled`
  // counter. No-op when no overlay scope is active.
  overlayCulled(): void;
  overlaySizeCulled(): void;

  // Called by element drawers (e.g. `LayoutEntity.redrawElements`) that do
  // their own viewport/size culling on non-path cell geometry and skip the
  // draw. Bumps `stats.elements.culled` / `.sizeCulled` respectively. These
  // counters sit alongside the sprite/vector/overlay/direct buckets and
  // represent work avoided, not work done.
  elementCulled(): void;
  elementSizeCulled(): void;

  // Optional access to per-frame render stats. Null on backends that don't
  // track stats.
  readonly stats: RenderStats | null;

  // The core drawing primitives
  bbox(data: graphics.Bbox, s: ShapeStyle): unknown;
  rect(data: graphics.Rectangle, s: ShapeStyle): unknown;
  arc(data: graphics.Arc, s: ShapeStyle): unknown;
  ellipse(data: graphics.Ellipse, s: ShapeStyle): unknown;
  path(data: graphics.Path, s: ShapeStyle): unknown;
  polygon(data: graphics.Polygon, s: ShapeStyle): unknown;
  // Text is weird. It needs a parent to determine orientation.
  // And has different styling objects.
  text(data: graphics.Text, s: TextStyle, parent: HasXform): unknown;

  // The union types
  shape(data: graphics.Shape, s: ShapeStyle): unknown;
  element(data: graphics.Element, s: Style, parent: HasXform): unknown;

  // Collections
  shapes(data: Array<graphics.Shape>, s: ShapeStyle): unknown;
  elements(data: Array<graphics.Element>, s: Style, parent: HasXform): unknown;

  // Instanced drawing. Returns a discriminated result describing only *this*
  // call's outcome — nested `instance()` calls inside `fn` do NOT influence
  // the return value here, so callers can rely on the result to decide
  // whether to draw per-instance overlays.
  instance(
    key: object,
    bbox: model.Bbox,
    xform: model.Xform,
    fn: RunFn
  ): InstanceDrawResult;
}

export type RunFn = (to: DrawTo) => unknown;
export type HasXform = { xform: model.Xform };

//
// # InstanceDrawResult
//
// The outcome of a `DrawTo.instance()` call, returned **only for that call's
// own decision** — never propagated from nested `instance()` calls inside
// `fn`. Callers use this to decide whether to emit overlays (routes, labels,
// highlights, outlines) around the instance.
//
// - `"drawn"`           — the instance was drawn (sprite or vector).
// - `"viewport-culled"` — the instance's bbox does not intersect the viewport.
//                         Its body was not drawn. Note: overlays whose extent
//                         exceeds `abs.bbox` (e.g. routes stubbing out the
//                         side of a cell) may still be visible; see callers
//                         for how this is handled.
// - `"size-culled"`     — the instance's bbox would render below the minimum
//                         screen-pixel threshold. Overlays on it would be
//                         sub-pixel too, so skipping them is safe.
//
export type InstanceDrawResult = "drawn" | "viewport-culled" | "size-culled";

//
// # Layout Display Options
//
// Controls visibility of metadata overlays on layouts (pins, labels, blockages).
// Propagated as a single object reference through the rendering pipeline.
//
export interface LayoutDisplayOptions {
  // Top-level category visibility — when false, the whole category is skipped
  // during rendering (and the indented sub-toggles below it are inert).
  instances: boolean;
  pins: boolean;
  routes: boolean;
  blockages: boolean;
  // Sub-item overlays — only meaningful when the parent category is visible.
  instanceLabels: boolean;
  pinLabels: boolean;
  routeLabels: boolean;
}

export const LayoutDisplayOptions = {
  // Default options: all categories visible; instance labels and blockages
  // off by default to match prior behavior.
  default(): LayoutDisplayOptions {
    return {
      instances: true,
      pins: true,
      routes: true,
      blockages: false,
      instanceLabels: false,
      pinLabels: true,
      routeLabels: true,
    };
  },
};

//
// # Schematic Display Options
//
// Controls visibility of text labels on schematics.
// Propagated as a single object reference through the rendering pipeline.
//
export interface SchematicDisplayOptions {
  // Top-level category visibility — when false, the whole category is skipped
  // during rendering (and the indented sub-toggles below it are inert).
  instances: boolean;
  pins: boolean;
  wires: boolean;
  // Sub-item overlays — only meaningful when the parent category is visible.
  instNames: boolean;
  cellNames: boolean;
  instParams: boolean;
  portNames: boolean;
  portDebug: boolean;
  wireLabels: boolean;
}

export const SchematicDisplayOptions = {
  // Default options: all categories visible, all labels visible except debug.
  default(): SchematicDisplayOptions {
    return {
      instances: true,
      pins: true,
      wires: true,
      instNames: true,
      cellNames: true,
      instParams: true,
      portNames: true,
      portDebug: false,
      wireLabels: true,
    };
  },
};

//
// # Selectability options
//
// Per-category selection gates. When a flag is false, items of that category
// cannot be picked by click, box-select, or select-all. Items still render
// normally — locking is purely behavioral.
//
export interface SchematicSelectability {
  instances: boolean;
  wires: boolean;
  pins: boolean;
}

export const SchematicSelectability = {
  default(): SchematicSelectability {
    return {
      instances: true,
      wires: true,
      pins: true,
    };
  },
};

export interface LayoutSelectability {
  instances: boolean;
  pins: boolean;
  routes: boolean;
  blockages: boolean;
}

export const LayoutSelectability = {
  default(): LayoutSelectability {
    return {
      instances: true,
      pins: true,
      routes: true,
      blockages: true,
    };
  },
};

// Minimum screen-pixel size for rendering. Elements whose bounding box is
// smaller than this in BOTH X and Y dimensions are skipped during drawing.
export const MIN_CULL_SCREEN_PX = 4;

// Minimum screen-pixel font height for labels. Text smaller than this is
// illegible on screen, so we skip drawing it rather than wasting work on
// unreadable pixels. This is deliberately larger than MIN_CULL_SCREEN_PX
// because legibility requires more pixels than mere visibility.
export const MIN_LABEL_SCREEN_PX = 10;

// Max total pixel area for a sprite canvas. Browsers silently zero canvases that
// exceed their GPU memory budget (~268M pixels in Chrome). Use a conservative
// limit well below that to avoid invisible sprites.
export const MAX_PIXEL_AREA = 128_000_000;
